import { de, enUS, Locale } from 'date-fns/locale';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum Language {
    ENGLISH = 'ENGLISH',
    GERMAN = 'GERMAN',
}

export const getDateLocaleByLocale = (locale: 'en' | 'de' | Language) => {
    const locales: Record<'en' | 'de' | Language, Locale> = {
        'en': enUS,
        'ENGLISH': enUS,

        'de': de,
        'GERMAN': de,
    };
    return locales[locale];
}

const translations = {
    [Language.ENGLISH]: {
        locale: 'en',

        monday: 'Monday',
        mondayShort: 'Mon',
        
        tuesday: 'Tuesday',
        tuesdayShort: 'Tue',
        
        wednesday: 'Wednesday',
        wednesdayShort: 'Wed',
        
        thursday: 'Thursday',
        thursdayShort: 'Thu',
        
        friday: 'Friday',
        fridayShort: 'Fri',
        
        saturday: 'Saturday',
        saturdayShort: 'Sat',
        
        sunday: 'Sunday',
        sundayShort: 'Sun',
        
        continue: 'Continue',

        location: 'Location',
        clients: 'clients',
        ourDoctors: 'Our doctors',
        reviews: 'Reviews',
        contact: 'Contact',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        closed: 'Closed',
        openingHours: 'Opening Hours',
        seeAvailableAppointments: 'See available appointments',
        selectClinic: 'Select clinic',
        selectStoreButton: 'Select',
        changeStoreButton: 'Change',

        services: 'services',
        treatments: 'treatments',
        searchTreatments: 'Search treatments',
        addTreatmentsToContinue: 'Add treatments to continue',
        bookTreatments: 'Book treatments',
        totalPrice: 'Total price',
        total: 'Total',
        free: 'Free',
        startingAt: 'Starting at',
        add: 'Add',

        timeAndDate: 'Time and date',
        freeSlots: 'free',
        noSlots: 'No slots',
        availableAppointments: 'Available appointments',
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        oClock: '',
        selectATimeslotToContinue: 'Select a timeslot to continue',
        showMore: 'Show more',
        showLess: 'Show less',
        more: 'more',
        noAppointmentsAvailableJoinWaitlist: 'No appointments available on this day. Would you like to join the waitlist?',
        noAppointmentsPleaseSelectOtherTime: "No appointments available at this time. Please select a different time.",
        joinWaitlistFor: 'Join waitlist for', // <date> comes right after this, with a space in between
        wantAnEarlierAppointment: 'Looking for an earlier time?',
        putYourselfOnWaitlist: 'Put yourself on our waitlist.', // Link to put on waitlist will be for this text.
        waitlistSignupSuccess: "You have successfully been added to our waitlist.",
        waitlistSignupError: "Something went wrong when signing up for our waitlist. Please try again.",
        preferredContactTimesLabel: 'Preferred contact times (optional)',
        contactTimeMorning: "Morning (08:00 - 12:00)",
        contactTimeAfternoon: "Afternoon (12:00 - 16:00)",
        contactTimeEvening: "Abend (16:00 - 22:00)",
        submitWaitlistButton: "Submit",
        waitlistFormTitle: "Waitlist - Stay In Touch",
        waitlistFormSubtitle: "We will notify you by SMS and E-Mail if an earlier time becomes available for your selected service.",

        personalDetails: 'Personal details',
        
        firstNameLabel: 'First name',
        enterFirstNamePlaceholder: 'Enter first name',
        firstNameRequired: 'First name is required',
        firstNameHelpMessage: 'Please enter a valid name (2-50 characters)',
        
        lastNameLabel: 'Last name',
        enterLastNamePlaceholder: 'Enter last name',
        lastNameRequired: 'Last name is required',
        lastNameHelpMessage: 'Please enter a valid name (2-50 characters)',
        
        emailAddressLabel: 'Email address',
        enterEmailAddressPlaceholder: 'Enter email address',
        emailAddressRequired: 'Email is required',
        emailAddressHelpMessage: 'Please enter a valid email address',
        
        phoneNumberLabel: 'Phone number',
        enterPhoneNumberPlaceholder: 'Enter your number',
        phoneNumberRequired: 'Phone number is required',
        phoneNumberHelpMessage: 'Please enter a valid phone number',
        
        wantEarlierTimeslotLabel: 'I would like an earlier appointment if one becomes available',
        
        messageLabel: 'Message (optional)',
        enterMessagePlaceholder: 'Leave a message or ask anything...',
        // Here, {AGB's|AGB} will give you "AGB's" text with AGB link
        // and {Datenschutzbestimmungen|DATENSCHUTZ} will give you "Datenschutzbestimmungen" text with the DATENSCHUTZ link.
        iAcceptTermsAndConditions: "I accept the {terms & conditions|AGB} and {privacy policy|DATENSCHUTZ}.*",
        reviewBooking: 'Review booking',
        fillOutYourInformationToContinue: 'Fill out your information to continue.',

        yourTreatments: 'Your treatments',
        emptyCart: 'Your cart is empty',
        addTreatmentsToGetStarted: 'Add treatments to get started',

        treatmentPackagePopupTitle: 'This treatment is available as a package',
        treatmentPackagePopupDescription: 'This package deal is cheaper than the stand-alone treatment and supports a more succesful treatment result.',
        inPackage: 'in a package',
        perTreatment: 'per Treatment',
        treatmentPackageSave: 'Save',
        toastAddedItemToCart: 'Successfully added package upgrade to your cart!',
        upgradeToPackageDealButton: 'Book Package',
        skipUpgradeToPackageDealButton: 'Skip',

        appointmentReview: 'Appointment review',
        confirmBooking: 'Confirm booking',
        discountCodeLabel: 'Discount code',
        discountCodePlaceholder: 'Enter discount code',
        discountCodeCaption: 'This will be applied in-person at the clinic.',
        bookingErrorTheDesiredSlotIsNotAvailableAnymore: 'The desired slot is not available anymore. Please choose a different timeslot.',
        bookingErrorUnknown: 'The desired slot could not be booked. Please choose a different time slot.',
        bookingErrorInvalidSelectedData: 'The booking could not be made because no timeslot was selected. Please go back and select a timeslot.',
        bookingErrorEmailBlocked: 'You are not able to book online. Please contact the clinic in order to schedule an appointment directly.',

        bookingConfirmedTitle: 'Thank you for booking!',
        bookingConfirmedInstructions: 'Your appointment has been successfully booked. You will receive a confirmation email shortly.',

        backToBeginning: 'Back to beginning',

        // Login
        loginTitle: 'Welcome Back',
        loginSubtitle: 'Enter your email to receive a login link',
        loginEmailPlaceholder: 'Enter your email address',
        loginSendMagicLink: 'Send Login Link',
        loginSendingMagicLink: 'Sending...',
        loginMagicLinkSent: 'Check your email! We sent you a login link.',
        loginMagicLinkError: 'No account found with this email address.',
        loginGenericError: 'Something went wrong. Please try again.',
        loginBackToBooking: 'Back to booking',
        loginVerifying: 'Verifying your login...',
        loginVerificationFailed: 'Login verification failed. The link may be expired.',
        loginTryAgain: 'Try again',
        loginPromptText: 'Been here before?',
        loginPromptLink: 'Log in',
        loggedInAs: 'Logged in as',
        logout: 'Log out',

        // Treatment Plan Card
        yourTreatmentPlan: 'Your Treatment Plan',
        viewYourPersonalizedPlan: 'View your personalized skin journey',

        loadingYourTimeslot: 'Loading your timeslot',
        pleaseWaitWhileWeLoadYourAvailableTimeslots: 'Please wait while we load your available timeslots',
        timeslotNoLongerAvailable: 'Timeslot no longer available',
        timeslotAlreadyTakenByOtherCustomer: 'This timeslot has already been taken by another customer.',
        checkForNewTimeslots: 'Check for new timeslots',
        appointmentConfirmed: 'Appointment confirmed!',
        yourAppointmentHasBeenSuccessfullyBooked: 'Your appointment has been successfully booked.',
        service: 'Service',
        confirmationEmailSentTo: 'A confirmation email has been sent to',
        claimYourTimeslot: 'Claim your timeslot',
        greatNewsTimeslotAvailable: 'Great news! A timeslot has become available.',
        bookedFor: 'Booked for',
        availableTimeslots: 'Available timeslots',
        discountCode: 'Discount code',
        optional: 'optional',
        enterDiscountCode: 'Enter discount code',
        messageToSalon: 'Message to salon',
        anySpecialRequests: 'Any special requests or questions?',
        bookAppointment: 'Book appointment',
        byBookingYouConfirmAvailability: 'By booking, you confirm this timeslot is still available.',
        waitlistClaimAllContactFieldsRequired: 'All fields below are required to book.',
        fieldRequiredShort: '(required)',
        waitlistWithdrawNotificationsLabel: 'Stop receiving notifications for this waitlist entry',
        waitlistWithdrawConfirmTitle: 'Withdraw from waitlist?',
        waitlistWithdrawConfirmDescription:
            'You will no longer receive emails about available slots for this request. This cannot be undone.',
        waitlistWithdrawConfirmButton: 'Yes, withdraw',
        waitlistWithdrawCancelButton: 'Cancel',
        waitlistWithdrawSuccess: 'You will no longer receive notifications for this waitlist entry.',
        waitlistWithdrawError: 'Could not withdraw. Please check your email or try again.',
        waitlistEntryNotAvailable: 'This waitlist link is invalid or no longer active.',
        waitlistWithdrawnTitle: 'Notifications stopped',
        waitlistWithdrawnDescription: 'You will no longer receive messages about this waitlist entry.',
        waitlistLoginRequired: 'Login required',
        waitlistLoginDescription: 'Please log in to view your available timeslots. We will send you a link via email.',
        waitlistSendLoginLink: 'Send login link',
        waitlistLoginLinkSent: 'A login link has been sent to your email. Please check your inbox.',
        waitlistLoginLinkError: 'Could not send login link. Please try again.',
        appointmentBookedSuccessfully: 'Appointment booked successfully!',
        appointmentBookingFailed: 'Appointment booking failed. The timeslot may no longer be available.',
        waitlistDuplicateAppointmentTitle: 'You already have this appointment booked',
        waitlistDuplicateAppointmentWouldMoveLabel: 'Appointment that would be moved',
        waitlistDuplicateAppointmentNewTimeLabel: 'New time',
        waitlistDuplicateAppointmentDescription:
            'Choose whether to move that appointment to the waitlist time you selected, or keep it and book an additional appointment.',
        waitlistRescheduleExistingButton: 'Move my existing appointment',
        waitlistBookNewAppointmentButton: 'Book a new appointment',
        clinic: 'Clinic',


        preferredPractitionerPlaceholder: 'Choose your practitioner',
        preferredPractitionerNoPreference: 'No preference',
        preferredPractitionerInfoPast: 'You can choose from all practitioners who have treated you before.',
        preferredPractitionerInfoCombined: 'For combined appointments with medical and cosmetic services, you can currently only choose the practitioner for the medical service.',

        deeplinkNotFound: "Link Not Found",
        deeplinkNotFoundDescription: 'The booking link you followed is not valid or has expired.',
        deeplinkLocationAndServiceContinueToBookingButton: 'Continue to booking',

        appointmentProgressAppointment: 'APPOINTMENT',
        appointmentProgressDetails: 'APPOINTMENT DETAILS',
        appointmentProgressChange: 'CHANGE APPOINTMENT',
        appointmentLoading: 'Loading...',
        appointmentErrorTitle: 'Error',
        appointmentErrorLoading: 'Your appointment details could not be loaded. Please try again later.',
        appointmentErrorNotFound: 'The requested appointment could not be found.',
        appointmentTooLateNotice: 'This appointment takes place in less than {hours} hours and can no longer be rescheduled or cancelled.',
        appointmentCannotBeChanged: 'This appointment can no longer be changed.',
        appointmentStatusTitle: 'Status',
        appointmentStatusScheduled: 'Scheduled',
        appointmentStatusRequested: 'Requested',
        appointmentStatusCancelled: 'Cancelled',
        appointmentStatusCompleted: 'Completed',
        appointmentStatusUnknown: 'Unknown',
        appointmentTreatmentsTitle: 'Treatments',
        appointmentNoTreatmentsListed: 'No specific treatments listed',
        appointmentAdditionalOptions: 'Additional options',
        appointmentMessageFromClinic: 'Message from the clinic',
        appointmentSelectNewDate: 'Select a new date',
        appointmentChangeButton: 'CHANGE APPOINTMENT',
        appointmentChangeServicesButton: 'Change treatments',
        appointmentOriginalTimeUnavailable: 'Your previous time is no longer available with these treatments. Please choose a new time.',
        appointmentChangeServicesProgress: 'CHANGE SERVICES',
        appointmentSelectTimeForServices: 'Choose a time for your services',
        appointmentServicesUnchanged: 'Change or add a service to continue.',
        appointmentBackToServices: 'Back to services',
        appointmentConfirmServicesTitle: 'Update your services?',
        appointmentConfirmServicesQuestion: 'Your appointment stays at the same time. The services will be updated.',
        appointmentConfirmServicesButton: 'UPDATE SERVICES',
        appointmentConfirmServices: 'Update services',
        appointmentCancelButton: 'Cancel appointment',
        appointmentAreYouSure: 'Are you sure?',
        appointmentCancelConfirmQuestion: 'Are you sure you want to cancel your appointment?',
        appointmentChangeConfirmQuestion: 'Are you sure you want to change your appointment?',
        appointmentReasonLabel: 'Reason for the cancellation*',
        appointmentReasonPlaceholder: 'Select a reason...',
        appointmentNoteLabel: 'Note (optional)',
        appointmentNotePlaceholder: 'Tell us the reason for your cancellation or change',
        appointmentDialogCancel: 'Cancel',
        appointmentFindNewTime: 'Reschedule appointment',
        appointmentAdjustAppointment: 'Adjust appointment',
        appointmentConfirmNewTimeButton: 'CONFIRM NEW TIME',
        appointmentConfirmNewTime: 'Confirm new time',
        appointmentBackToDetails: 'Back to details',
        appointmentSaveChangesButton: 'SAVE CHANGES',
        appointmentDiscardChanges: 'Discard changes',
        appointmentCancelling: 'Cancelling...',
        appointmentRetentionHealthTitle: 'Get well soon!',
        appointmentRetentionHealthBody: "Your health comes first! Once you're feeling better, we'd love to see you. You can easily move your appointment to a later time, or cancel it if you need to.",
        appointmentRetentionSchedulingConflictTitle: "Let's find a better time!",
        appointmentRetentionSchedulingConflictBody: "Sometimes life gets in the way — we know how that is! Simply reschedule your appointment to a time that fits your day better. If you don't know yet when it will work for you, you can of course also cancel.",
        appointmentRetentionTreatmentAnxietyTitle: "We're here for you!",
        appointmentRetentionTreatmentAnxietyBody: "Do you still have questions, or are you unsure about your treatment? We understand. Our team is happy to take the time to answer your questions. You can also book a consultation first. If you then decide to go ahead with the treatment, we'll credit the consultation cost toward it.",
        appointmentBookConsultation: 'Book a consultation',
        appointmentRetentionFinancialTitle: 'More flexibility for you',
        appointmentRetentionFinancialBody: 'Did you know you can also pay with Klarna — flexibly at a later time or in instalments? That way you can decide what works best for you.',
        appointmentPayWithKlarna: 'Pay with Klarna',
        appointmentKlarnaMinimumNotMet: 'Only available for bookings of {amount} EUR or more.',
        appointmentRetentionPregnancyTitle: 'Your mama glow',
        appointmentRetentionPregnancyBody: "You don't have to skip your me-time during pregnancy or while breastfeeding! Our Mama Facial is tailored to what your skin needs in this special time. If you'd like, you can switch your appointment to it right away.",
        appointmentBookMamaFacial: 'Book the Mama Facial',
        appointmentRetentionBookingErrorTitle: 'No problem, that can be changed!',
        appointmentRetentionBookingErrorBody: 'Selected the wrong treatment or forgot your discount code? No problem! You can make many changes right here in self-service. And if you need help, our team is happy to assist.',
        appointmentRetentionOtherClinicTitle: 'Maybe see you soon!',
        appointmentRetentionOtherClinicBody: "Thank you for letting us know! We wish you all the best for your treatment. And if you'd like to get to know us in the future, we'd love to welcome you.",
        appointmentRetentionNoLongerNeededTitle: 'All right!',
        appointmentRetentionNoLongerNeededBody: "Thank you for letting us know! Whenever you feel like a treatment again, we're happy to be there for you.",
        appointmentRetentionPractitionerUnavailableTitle: "We'll find the right solution!",
        appointmentRetentionPractitionerUnavailableBody: "Would you rather book your appointment with a specific person on our team? We understand! In our app you can easily choose appointments with practitioners you've seen before. And with every other expert on our team, you're in the best hands too. If you have questions or special requests, we're always happy to help.",
        appointmentRetentionDefaultTitle: 'Cancel appointment',
        appointmentRetentionDefaultBody: "We're sorry you want to cancel your appointment (reason: {reasonLabel}). Do you really want to cancel?",
        appointmentToastCancelled: 'Your appointment has been cancelled',
        appointmentToastCancelError: 'Your appointment could not be cancelled. Please try again.',
        appointmentToastChanged: 'Your appointment has been changed',
        appointmentToastServicesChanged: 'Your services have been updated',
        appointmentToastServicesError: 'The services could not be updated. Please try again.',
        appointmentToastSlotUnavailable: 'This time is no longer available. Please choose another one.',
        appointmentToastChangeError: 'Your appointment could not be changed. Please try again.',
        appointmentToastSaved: 'Your changes have been saved',
        appointmentToastSaveError: 'Your changes could not be saved. Please try again.',
        appointmentToastKlarnaAuthorized: 'Your Klarna payment has been authorized.',
        appointmentToastKlarnaFailed: 'The Klarna payment was declined or cancelled.',
        appointmentToastKlarnaStartError: 'Could not start Klarna payment.',
        appointmentToastSelectDateFirst: 'Please select a new date and time first.',
        appointmentAvailabilitiesError: 'The available appointments could not be loaded. Please adjust the time range.',
    },
    [Language.GERMAN]: {
        locale: 'de',

        monday: 'Montag',
        mondayShort: 'Mo',
        
        tuesday: 'Dienstag',
        tuesdayShort: 'Di',
        
        wednesday: 'Mittwoch',
        wednesdayShort: 'Mi',
        
        thursday: 'Donnerstag',
        thursdayShort: 'Do',
        
        friday: 'Freitag',
        fridayShort: 'Fr',
        
        saturday: 'Samstag',
        saturdayShort: 'Sa',
        
        sunday: 'Sonntag',
        sundayShort: 'So',
        
        continue: 'Weiter',

        location: 'Standort',
        clients: 'Kunden',
        ourDoctors: 'Unsere Ärzte',
        reviews: 'Bewertungen',
        contact: 'Kontakt',
        address: 'Adresse',
        phone: 'Telefon',
        email: 'Email',
        openingHours: 'Öffnungszeiten',
        closed: 'Geschlossen',
        seeAvailableAppointments: 'Termine anzeigen',
        selectClinic: 'Klinik auswählen',
        selectStoreButton: 'Auswählen',
        changeStoreButton: 'Ändern',
        
        services: 'Leistungen',
        treatments: 'Behandlungen',
        searchTreatments: 'Behandlung suchen',
        addTreatmentsToContinue: 'Behandlung hinzufügen',
        bookTreatments: 'Behandlungen buchen',
        totalPrice: 'Gesamtpreis',
        total: 'Gesamt',
        free: 'Kostenlos',
        startingAt: 'Ab',
        add: 'Hinzufügen',

        timeAndDate: 'Zeit und Datum',
        freeSlots: 'frei',
        noSlots: 'Keine Termine',
        availableAppointments: 'Verfügbare Termine',
        morning: 'Vormittag',
        afternoon: 'Nachmittag',
        evening: 'Abend',
        oClock: 'Uhr',
        selectATimeslotToContinue: 'Termin auswählen',
        showMore: 'Mehr anzeigen',
        showLess: 'Weniger anzeigen',
        more: 'mehr',
        noAppointmentsAvailableJoinWaitlist: 'Keine Termine verfügbar. Möchtest Du Dich in die Warteliste eintragen?',
        noAppointmentsPleaseSelectOtherTime: "Keine Termine verfügbar. Bitte wählen Sie eine andere Zeit.",
        joinWaitlistFor: 'Warteliste für', // <date> comes right after this, with a space in between
        wantAnEarlierAppointment: 'Du hast Interesse an einem früheren Termin?',
        putYourselfOnWaitlist: 'Dann setze Dich auf unsere Warteliste.', // Link to put on waitlist will be for this text.
        waitlistSignupSuccess: "Danke! Du stehst jetzt auf unserer Warteliste.",
        waitlistSignupError: "Bei der Anmeldung zu unserer Warteliste ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
        preferredContactTimesLabel: 'Wann können wir Dich am besten erreichen? (optional)',
        contactTimeMorning: "Vormittag (08:00 - 12:00 Uhr)",
        contactTimeAfternoon: "Nachmittag (12:00 - 16:00 Uhr)",
        contactTimeEvening: "Abend (16:00 - 22:00 Uhr)",
        submitWaitlistButton: "Auf Warteliste setzen",
        waitlistFormTitle: "Warteliste - Stay In Touch",
        waitlistFormSubtitle: "Wir informieren Sie umgehend per SMS und E-Mail, sobald ein früherer Termin für Ihre gewünschte Behandlung verfügbar wird.",

        personalDetails: 'Persönliche Informationen',

        firstNameLabel: 'Vorname',
        enterFirstNamePlaceholder: 'Vorname eingeben',
        firstNameRequired: 'Vorname ist erforderlich',
        firstNameHelpMessage: 'Bitte gebe einen gültigen Namen ein (2-50 Zeichen)',
        
        lastNameLabel: 'Nachname',
        enterLastNamePlaceholder: 'Nachname eingeben',
        lastNameRequired: 'Nachname ist erforderlich',
        lastNameHelpMessage: 'Bitte gebe einen gültigen Namen ein (2-50 Zeichen)',
        
        emailAddressLabel: 'Email-Adresse',
        enterEmailAddressPlaceholder: 'Email-Adresse eingeben',
        emailAddressRequired: 'Email-Adresse ist erforderlich',
        emailAddressHelpMessage: 'Bitte gebe eine gültige Email-Adresse ein',
        
        phoneNumberLabel: 'Telefonnummer',
        enterPhoneNumberPlaceholder: 'Telefonnummer eingeben',
        phoneNumberRequired: 'Telefonnummer ist erforderlich',
        phoneNumberHelpMessage: 'Bitte gebe eine gültige Telefonnummer ein',

        wantEarlierTimeslotLabel: 'Ich möchte einen früheren Termin, falls dieser verfügbar wird',

        messageLabel: 'Nachricht (optional)',
        enterMessagePlaceholder: 'Hinterlasse eine Nachricht oder stelle eine Frage...',
        // Here, {AGB's|AGB} will give you "AGB's" text with AGB link
        // and {Datenschutzbestimmungen|DATENSCHUTZ} will give you "Datenschutzbestimmungen" text with the DATENSCHUTZ link.
        iAcceptTermsAndConditions: "Ich stimme den {AGB's|AGB} und {Datenschutzbestimmungen|DATENSCHUTZ} zu.*",
        reviewBooking: 'Buchung überprüfen',
        fillOutYourInformationToContinue: 'Bitte fülle Deine Informationen aus, um fortzufahren.',

        yourTreatments: 'Deine Behandlungen',
        emptyCart: 'Dein Warenkorb ist leer',
        addTreatmentsToGetStarted: 'Füge Behandlungen hinzu, um loszulegen',

        treatmentPackagePopupTitle: 'Diese Behandlung ist als Bundle verfügbar',
        treatmentPackagePopupDescription: 'Dieses Bundleangebot ist günstiger als die Einzelbehandlung und unterstützt einen besseren Behandlungserfolg.',
        inPackage: 'im Bundle',
        perTreatment: 'pro Behandlung',
        treatmentPackageSave: 'Spare',
        toastAddedItemToCart: 'Bundle-Upgrade erfolgreich zum Warenkorb hinzugefügt!',
        upgradeToPackageDealButton: 'Bundle buchen',
        skipUpgradeToPackageDealButton: 'Überspringen',

        appointmentReview: 'Buchung überprüfen',
        confirmBooking: 'Buchung bestätigen',
        discountCodeLabel: 'Rabattcode',
        discountCodePlaceholder: 'Rabattcode eingeben',
        discountCodeCaption: 'Dieser wird persönlich in der Klinik angewendet.',
        bookingErrorTheDesiredSlotIsNotAvailableAnymore: 'Der gewünschte Termin ist nicht mehr verfügbar. Bitte wähle einen anderen Termin.',
        bookingErrorUnknown: 'Die Buchung konnte nicht erstellt werden. Wähle bitte einen anderen Termin.',
        bookingErrorInvalidSelectedData: 'Die Buchung konnte nicht erstellt werden, weil kein Termin ausgewählt wurde. Bitte gehe zurück und wähle einen Termin.',
        bookingErrorEmailBlocked: 'Eine Online-Buchung ist nicht möglich. Bitte kontaktiere die Klinik, um direkt einen Termin zu vereinbaren.',

        bookingConfirmedTitle: 'Danke für Deine Buchung!',
        bookingConfirmedInstructions: 'Deine Buchung wurde erstellt. Du erhältst in Kürze eine Bestätigung per E-Mail.',

        backToBeginning: 'Zurück zum Start',

        // Login
        loginTitle: 'Willkommen zurück',
        loginSubtitle: 'Gib Deine E-Mail-Adresse ein, um einen Login-Link zu erhalten',
        loginEmailPlaceholder: 'E-Mail-Adresse eingeben',
        loginSendMagicLink: 'Login-Link senden',
        loginSendingMagicLink: 'Wird gesendet...',
        loginMagicLinkSent: 'Prüfe Deine E-Mails! Wir haben Dir einen Login-Link gesendet.',
        loginMagicLinkError: 'Kein Konto mit dieser E-Mail-Adresse gefunden.',
        loginGenericError: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
        loginBackToBooking: 'Zurück zur Buchung',
        loginVerifying: 'Login wird überprüft...',
        loginVerificationFailed: 'Login-Überprüfung fehlgeschlagen. Der Link ist möglicherweise abgelaufen.',
        loginTryAgain: 'Erneut versuchen',
        loginPromptText: 'Schon mal bei uns gewesen?',
        loginPromptLink: 'Einloggen',
        loggedInAs: 'Eingeloggt als',
        logout: 'Ausloggen',

        // Treatment Plan Card
        yourTreatmentPlan: 'Dein Treatment Plan',
        viewYourPersonalizedPlan: 'Hier zu Deinen persönlichen Empfehlungen.',

        loadingYourTimeslot: 'Lade Deinen Termin',
        pleaseWaitWhileWeLoadYourAvailableTimeslots: 'Bitte warte, während wir Deine verfügbaren Termine laden',
        timeslotNoLongerAvailable: 'Termin nicht mehr verfügbar',
        timeslotAlreadyTakenByOtherCustomer: 'Dieser Termin wurde bereits von einem anderen Kunden gebucht.',
        checkForNewTimeslots: 'Nach neuen Terminen suchen',
        appointmentConfirmed: 'Termin bestätigt!',
        yourAppointmentHasBeenSuccessfullyBooked: 'Dein Termin wurde erfolgreich gebucht.',
        service: 'Behandlung',
        confirmationEmailSentTo: 'Eine Bestätigungs-E-Mail wurde gesendet an',
        claimYourTimeslot: 'Kurzfristiger Termin verfügbar',
        greatNewsTimeslotAvailable: 'Gute Nachrichten! Ein Termin ist verfügbar geworden.',
        bookedFor: 'Gebucht für',
        availableTimeslots: 'Freie Termine:',
        discountCode: 'Rabattcode',
        optional: 'optional',
        enterDiscountCode: 'Rabattcode eingeben',
        messageToSalon: 'Nachricht an den Salon',
        anySpecialRequests: 'Besondere Wünsche oder Fragen?',
        bookAppointment: 'Termin buchen',
        byBookingYouConfirmAvailability: 'Mit der Buchung bestätigst Du, dass dieser Termin noch verfügbar ist.',
        waitlistClaimAllContactFieldsRequired: 'Alle folgenden Felder sind für die Buchung erforderlich.',
        fieldRequiredShort: '(Pflichtfeld)',
        waitlistWithdrawNotificationsLabel: 'Keine Benachrichtigungen mehr erhalten für diesen Eintrag',
        waitlistWithdrawConfirmTitle: 'Von der Warteliste abmelden?',
        waitlistWithdrawConfirmDescription:
            'Du erhältst keine E-Mails mehr zu freien Terminen für diese Anfrage. Dies kann nicht rückgängig gemacht werden.',
        waitlistWithdrawConfirmButton: 'Ja, abmelden',
        waitlistWithdrawCancelButton: 'Abbrechen',
        waitlistWithdrawSuccess: 'Du erhältst keine Benachrichtigungen mehr zu diesem Wartelisten-Eintrag.',
        waitlistWithdrawError: 'Abmeldung fehlgeschlagen. Bitte E-Mail prüfen oder erneut versuchen.',
        waitlistEntryNotAvailable: 'Dieser Wartelisten-Link ist ungültig oder nicht mehr aktiv.',
        waitlistWithdrawnTitle: 'Benachrichtigungen beendet',
        waitlistWithdrawnDescription: 'Du erhältst keine weiteren Nachrichten zu diesem Wartelisten-Eintrag.',
        waitlistLoginRequired: 'Anmeldung erforderlich',
        waitlistLoginDescription: 'Bitte melde Dich an, um Deine verfügbaren Termine zu sehen. Wir senden Dir einen Link per E-Mail.',
        waitlistSendLoginLink: 'Login-Link senden',
        waitlistLoginLinkSent: 'Ein Login-Link wurde an Deine E-Mail-Adresse gesendet. Bitte überprüfe Dein Postfach.',
        waitlistLoginLinkError: 'Login-Link konnte nicht gesendet werden. Bitte versuche es erneut.',
        appointmentBookedSuccessfully: 'Termin erfolgreich gebucht!',
        appointmentBookingFailed: 'Terminbuchung fehlgeschlagen. Der Termin ist möglicherweise nicht mehr verfügbar.',
        waitlistDuplicateAppointmentTitle: 'Du hast diesen Termin bereits gebucht',
        waitlistDuplicateAppointmentWouldMoveLabel: 'Termin, der verschoben würde',
        waitlistDuplicateAppointmentNewTimeLabel: 'Neuer Zeitpunkt',
        waitlistDuplicateAppointmentDescription:
            'Du kannst diesen Termin auf die gewählte Wartezeiten-Zeit verschieben oder ihn behalten und einen weiteren Termin buchen.',
        waitlistRescheduleExistingButton: 'Bestehenden Termin verschieben',
        waitlistBookNewAppointmentButton: 'Neuen Termin buchen',
        clinic: 'Klinik',


        preferredPractitionerPlaceholder: 'Wähle Deine:n Behandler:in',
        preferredPractitionerNoPreference: 'Keine Präferenz',
        preferredPractitionerInfoPast: 'Du kannst zwischen allen Behandlern wählen, bei denen Du zuvor schon einmal behandelt wurdest.',
        preferredPractitionerInfoCombined: 'Bei Kombi-Terminen mit ärztlichen und kosmetischen Leistungen kannst Du aktuell nur den Behandler für die ärztliche Leistung wählen.',

        deeplinkNotFound: 'Link nicht gefunden',
        deeplinkNotFoundDescription: 'Der Buchungslink ist ungültig oder abgelaufen.',
        deeplinkLocationAndServiceContinueToBookingButton: 'Weiter zur Buchung',

        appointmentProgressAppointment: 'TERMIN',
        appointmentProgressDetails: 'TERMINDETAILS',
        appointmentProgressChange: 'TERMIN ÄNDERN',
        appointmentLoading: 'Laden...',
        appointmentErrorTitle: 'Fehler',
        appointmentErrorLoading: 'Deine Termindetails konnten nicht geladen werden. Bitte versuche es später erneut.',
        appointmentErrorNotFound: 'Der angeforderte Termin konnte nicht gefunden werden.',
        appointmentTooLateNotice: 'Dieser Termin findet in weniger als {hours} Stunden statt und kann nicht mehr verschoben oder storniert werden.',
        appointmentCannotBeChanged: 'Dieser Termin kann nicht geändert werden.',
        appointmentStatusTitle: 'Status',
        appointmentStatusScheduled: 'Geplant',
        appointmentStatusRequested: 'Angefragt',
        appointmentStatusCancelled: 'Storniert',
        appointmentStatusCompleted: 'Abgeschlossen',
        appointmentStatusUnknown: 'Unbekannt',
        appointmentTreatmentsTitle: 'Behandlungen',
        appointmentNoTreatmentsListed: 'Keine spezifischen Leistungen aufgeführt',
        appointmentAdditionalOptions: 'Weitere Optionen',
        appointmentMessageFromClinic: 'Nachricht vom Anbieter',
        appointmentSelectNewDate: 'Neues Datum auswählen',
        appointmentChangeButton: 'TERMIN ÄNDERN',
        appointmentChangeServicesButton: 'Behandlungen ändern',
        appointmentOriginalTimeUnavailable: 'Dein bisheriger Termin ist mit diesen Behandlungen nicht mehr verfügbar. Bitte wähle eine neue Zeit.',
        appointmentChangeServicesProgress: 'LEISTUNGEN ÄNDERN',
        appointmentSelectTimeForServices: 'Wähle eine Zeit für Deine Leistungen',
        appointmentServicesUnchanged: 'Ändere oder ergänze eine Leistung, um fortzufahren.',
        appointmentBackToServices: 'Zurück zu den Leistungen',
        appointmentConfirmServicesTitle: 'Leistungen aktualisieren?',
        appointmentConfirmServicesQuestion: 'Dein Termin bleibt zur gleichen Zeit. Die Leistungen werden aktualisiert.',
        appointmentConfirmServicesButton: 'LEISTUNGEN AKTUALISIEREN',
        appointmentConfirmServices: 'Leistungen aktualisieren',
        appointmentCancelButton: 'Termin absagen',
        appointmentAreYouSure: 'Bist Du Dir sicher?',
        appointmentCancelConfirmQuestion: 'Bist Du Dir sicher, dass Du den Termin absagen möchtest?',
        appointmentChangeConfirmQuestion: 'Bist Du Dir sicher, dass Du den Termin ändern möchtest?',
        appointmentReasonLabel: 'Grund für die Terminabsage*',
        appointmentReasonPlaceholder: 'Grund auswählen...',
        appointmentNoteLabel: 'Anmerkung (optional)',
        appointmentNotePlaceholder: 'Nenne uns den Grund für deine Terminabsage oder Umbuchung',
        appointmentDialogCancel: 'Abbrechen',
        appointmentFindNewTime: 'Termin umbuchen',
        appointmentAdjustAppointment: 'Termin anpassen',
        appointmentConfirmNewTimeButton: 'NEUE ZEIT BESTÄTIGEN',
        appointmentConfirmNewTime: 'Neue Zeit bestätigen',
        appointmentBackToDetails: 'Zurück zu Details',
        appointmentSaveChangesButton: 'ÄNDERUNGEN SPEICHERN',
        appointmentDiscardChanges: 'Änderungen verwerfen',
        appointmentCancelling: 'Wird abgesagt...',
        appointmentRetentionHealthTitle: 'Gute Besserung!',
        appointmentRetentionHealthBody: 'Deine Gesundheit geht vor! Wenn Du Dich wieder besser fühlst, freuen wir uns, Dich bei uns zu sehen. Du kannst Deinen Termin ganz einfach auf einen späteren Zeitpunkt verschieben oder bei Bedarf stornieren.',
        appointmentRetentionSchedulingConflictTitle: 'Lass uns einen besseren Zeitpunkt finden!',
        appointmentRetentionSchedulingConflictBody: 'Manchmal kommt das Leben dazwischen – kennen wir! Buche Deinen Termin einfach auf einen Zeitpunkt um, der besser in Deinen Alltag passt. Falls Du noch nicht weißt, wann es für Dich passt, kannst Du natürlich auch stornieren.',
        appointmentRetentionTreatmentAnxietyTitle: 'Wir sind für Dich da!',
        appointmentRetentionTreatmentAnxietyBody: 'Du hast noch Fragen oder bist Dir bei Deiner Behandlung unsicher? Das verstehen wir gut. Unser Team nimmt sich gerne Zeit für Dich und beantwortet Deine Fragen. Du kannst auch erst einmal einen Beratungstermin buchen. Wenn Du Dich anschließend für die Behandlung entscheidest, rechnen wir die Beratungskosten auf diese an.',
        appointmentBookConsultation: 'Beratungsgespräch buchen',
        appointmentRetentionFinancialTitle: 'Mehr Flexibilität für Dich',
        appointmentRetentionFinancialBody: 'Wusstest Du schon? Bei uns kannst Du auch mit Klarna bezahlen – ganz flexibel zu einem späteren Zeitpunkt oder in Raten. So kannst Du selbst entscheiden, was für Dich am besten passt.',
        appointmentPayWithKlarna: 'Mit Klarna bezahlen',
        appointmentKlarnaMinimumNotMet: 'Nur für Buchungen ab {amount} EUR verfügbar.',
        appointmentRetentionPregnancyTitle: 'Dein Mama-Glow',
        appointmentRetentionPregnancyBody: 'Auch während der Schwangerschaft oder Stillzeit musst Du nicht auf Deine Me-Time verzichten! Unser Mama Facial ist speziell auf die Bedürfnisse Deiner Haut in dieser besonderen Zeit abgestimmt. Wenn Du möchtest, buche Deinen Termin gleich darauf um.',
        appointmentBookMamaFacial: 'Mama Facial buchen',
        appointmentRetentionBookingErrorTitle: 'Kein Problem, das lässt sich ändern!',
        appointmentRetentionBookingErrorBody: 'Die falsche Behandlung ausgewählt oder den Rabattcode vergessen? Kein Problem! Viele Änderungen kannst Du direkt hier im Self-Service vornehmen. Und falls Du Unterstützung brauchst, hilft Dir unser Team gerne weiter.',
        appointmentRetentionOtherClinicTitle: 'Vielleicht bis bald!',
        appointmentRetentionOtherClinicBody: 'Danke, dass Du uns Bescheid gibst! Wir wünschen Dir alles Gute für Deine Behandlung. Und falls Du uns in Zukunft kennenlernen möchtest, freuen wir uns auf Dich.',
        appointmentRetentionNoLongerNeededTitle: 'Alles klar!',
        appointmentRetentionNoLongerNeededBody: 'Danke, dass Du uns Bescheid gibst! Wenn Du irgendwann wieder Lust auf eine Behandlung hast, sind wir gerne für Dich da.',
        appointmentRetentionPractitionerUnavailableTitle: 'Wir finden die passende Lösung!',
        appointmentRetentionPractitionerUnavailableBody: 'Du möchtest Deinen Termin lieber bei einer bestimmten Person aus unserem Team buchen? Das verstehen wir! Über unsere App kannst Du ganz einfach Termine bei Deinen bisherigen Behandler:innen auswählen. Aber auch bei allen anderen Expert:innen in unserem Team bist Du in den besten Händen. Bei Fragen oder besonderen Wünschen sind wir jederzeit gerne für Dich da.',
        appointmentRetentionDefaultTitle: 'Termin absagen',
        appointmentRetentionDefaultBody: 'Schade, dass Du Deinen Termin absagen möchtest (Grund: {reasonLabel}). Möchtest Du den Termin wirklich stornieren?',
        appointmentToastCancelled: 'Dein Termin wurde erfolgreich abgesagt',
        appointmentToastCancelError: 'Dein Termin konnte nicht abgesagt werden. Bitte versuche es erneut.',
        appointmentToastChanged: 'Dein Termin wurde erfolgreich geändert',
        appointmentToastServicesChanged: 'Deine Leistungen wurden aktualisiert',
        appointmentToastServicesError: 'Die Leistungen konnten nicht aktualisiert werden. Bitte versuche es erneut.',
        appointmentToastSlotUnavailable: 'Diese Zeit ist nicht mehr verfügbar. Bitte wähle eine andere.',
        appointmentToastChangeError: 'Dein Termin konnte nicht geändert werden. Bitte versuche es erneut.',
        appointmentToastSaved: 'Deine Änderungen wurden gespeichert',
        appointmentToastSaveError: 'Deine Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.',
        appointmentToastKlarnaAuthorized: 'Deine Klarna-Zahlung wurde autorisiert.',
        appointmentToastKlarnaFailed: 'Die Klarna-Zahlung wurde abgelehnt oder abgebrochen.',
        appointmentToastKlarnaStartError: 'Klarna-Zahlung konnte nicht gestartet werden.',
        appointmentToastSelectDateFirst: 'Bitte wähle zuerst ein neues Datum und eine Uhrzeit aus.',
        appointmentAvailabilitiesError: 'Fehler beim Laden der verfügbaren Termine. Bitte passe den Zeitraum an.',
    },
} as const;

type TranslationKey = keyof (typeof translations)[keyof typeof translations];
type TranslationValue = (typeof translations)[keyof typeof translations][TranslationKey];

type LanguageState = {
    language: Language;
    setLanguage: (language: Language) => void;
    t: (key: TranslationKey) => TranslationValue | TranslationKey;
}

export const useLanguageStore = create<LanguageState>()(
    persist(
        (set, get) => ({
            language: Language.GERMAN, // Default to German
            setLanguage: (language: Language) => set({ language }),
            t: (key) => {
                const state = get();
                const translation = translations[state.language][key];

                if (translation === undefined) {
                    console.warn(`Translation key "${key}" not found for language ${state.language}`);
                    return key;
                }

                return translation;
            },
        }),
        {
            name: 'language-storage',
        }
    )
);
